import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SEOData = {
  id: string;
  title: string;
  meta_description: string;
  social_image_url: string | null;
  twitter_card_type: string;
};

const SEO = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<SEOData>({
    id: "home",
    title: "",
    meta_description: "",
    social_image_url: null,
    twitter_card_type: "summary_large_image",
  });

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    try {
      const { data, error } = await supabase
        .from("seo")
        .select("*")
        .eq("id", "home")
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setFormData(data);
      } else {
        // Create default record if it doesn't exist
        setFormData({
          id: "home",
          title: "",
          meta_description: "",
          social_image_url: null,
          twitter_card_type: "summary_large_image",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch SEO settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `home-og-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("seo-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("seo-images").getPublicUrl(filePath);
      
      setFormData({ ...formData, social_image_url: data.publicUrl });
      toast({
        title: "Success",
        description: "Social image uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.meta_description) {
      toast({
        title: "Error",
        description: "Title and meta description are required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("seo")
        .upsert(formData, { onConflict: "id" });

      if (error) throw error;

      toast({
        title: "Success",
        description: "SEO settings saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to save SEO settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">Home Page SEO</h1>
          </div>
          <Button onClick={handleSubmit} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {loading ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              Loading...
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Home Page SEO</CardTitle>
              <CardDescription>
                Optimize your home page for search engines and social media sharing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="title">Page Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter page title (max 60 characters)"
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.title.length}/60 characters
                </p>
              </div>

              <div>
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Enter meta description (max 160 characters)"
                  rows={3}
                  maxLength={160}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.meta_description.length}/160 characters
                </p>
              </div>

              <div>
                <Label htmlFor="twitter_card_type">Twitter Card Type</Label>
                <Select
                  value={formData.twitter_card_type}
                  onValueChange={(value) => setFormData({ ...formData, twitter_card_type: value })}
                >
                  <SelectTrigger id="twitter_card_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="social_image">Social Media Image (Open Graph / Twitter)</Label>
                <Input
                  id="social_image"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="mb-2"
                />
                <p className="text-xs text-muted-foreground mb-2">
                  Recommended: 1200x630px for optimal display on social media
                </p>
                {formData.social_image_url && (
                  <div className="mt-2">
                    <img
                      src={formData.social_image_url}
                      alt="Social media preview"
                      className="w-full max-w-md rounded-lg border border-border"
                    />
                  </div>
                )}
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Preview</h4>
                <div className="space-y-1">
                  <p className="text-primary font-medium">{formData.title || "Page Title"}</p>
                  <p className="text-sm text-muted-foreground">
                    {formData.meta_description || "Meta description will appear here..."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SEO;
